import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  Chip,
  CircularProgress,
  Switch,
  Slider,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  Backup as BackupIcon,
  FolderOpen as FolderOpenIcon,
  SettingsBackupRestore as RestoreIcon,
  Wallpaper as WallpaperIcon,
  Delete as DeleteIcon,
  Key as KeyIcon
} from '@mui/icons-material';
import DatabaseService from '../services/database';
import { useLicense } from '../context/LicenseContext';
import { useWebAuth } from '../components/WebAuthGate';
import { EmailVerificationPanel } from '../components/EmailVerification';
import { IS_WEB } from '../services/platform';
import {
  BACKGROUND_KEY,
  BACKGROUND_FADE_KEY,
  DEFAULT_FADE,
  MIN_FADE,
  MAX_FADE,
  backgroundStyles,
  clampFade,
  processBackgroundFile
} from '../utils/pedigreeBackground';
import { getActivationFeedback } from '../utils/licenseActivation';

// The background image is a few hundred kilobytes; it is written the moment
// it changes, so the Save button (which rewrites every field) skips it.
const SAVE_ON_CHANGE_ONLY = new Set([BACKGROUND_KEY]);

const Settings = () => {
  const [settings, setSettings] = useState({
    loftName: '',
    loftSlogan: '',
    breederName: '',
    loftAddress: '',
    loftPhone: '',
    loftEmail: '',
    loftWebsite: '',
    // Optional background image printed behind every pedigree, plus the
    // strength of the white overlay that keeps the text on top readable.
    [BACKGROUND_KEY]: '',
    [BACKGROUND_FADE_KEY]: String(DEFAULT_FADE),
    // 'backupEnabled' (not the legacy 'autoBackup' key, which old versions
    // saved as 'false' from a dormant field with no UI).
    backupEnabled: true,
    backupLocation: '',
    backupKeep: '10'
  });
  const { state: licenseState, openPrompt, activate, recheck, reload: reloadLicense, deactivate, cancelSubscription } = useLicense();
  const { email: accountEmail, signOut } = useWebAuth();
  const [licenseBusy, setLicenseBusy] = useState(false);
  const [activationOpen, setActivationOpen] = useState(false);
  const [activationKey, setActivationKey] = useState('');
  const [activationBusy, setActivationBusy] = useState(false);
  const [activationFeedback, setActivationFeedback] = useState(null);
  const activationInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [backupFiles, setBackupFiles] = useState([]);
  const [appVersion, setAppVersion] = useState('');
  const [updateBusy, setUpdateBusy] = useState(false);
  const [updateResult, setUpdateResult] = useState('');
  const [backgroundBusy, setBackgroundBusy] = useState(false);

  useEffect(() => {
    loadSettings();
    loadBackupInfo();
    if (window.electronAPI && window.electronAPI.getAppVersion) {
      window.electronAPI.getAppVersion().then(setAppVersion).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (activationOpen && activationFeedback && !activationFeedback.complete && !activationBusy) {
      activationInputRef.current?.focus();
    }
  }, [activationOpen, activationFeedback, activationBusy]);

  const loadSettings = async () => {
    try {
      const settingsKeys = Object.keys(settings);
      const settingsData = {};
      
      for (const key of settingsKeys) {
        const value = await DatabaseService.getSetting(key);
        if (value !== null) {
          settingsData[key] = value;
        }
      }
      
      setSettings(prev => ({ ...prev, ...settingsData }));
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBackupInfo = async () => {
    if (!window.electronAPI || !window.electronAPI.backupStatus) return;
    try {
      setBackupInfo(await window.electronAPI.backupStatus());
    } catch (error) {
      console.error('Error loading backup status:', error);
    }
  };

  // Write a single setting straight away, for fields the Save button is the
  // wrong moment for. Returns the value as it was actually stored — the web
  // app rewrites an uploaded background into a /api/photos/ URL — so callers
  // can put THAT into state rather than the value they sent.
  const persistSetting = async (key, value) => {
    const previous = settings[key];
    handleSettingChange(key, value);
    try {
      const stored = await DatabaseService.setSetting(key, value);
      const settled = stored === undefined ? value : stored;
      if (settled !== value) handleSettingChange(key, settled);
      return settled;
    } catch (error) {
      // Nothing was written, so don't leave the page showing a setting the
      // database doesn't have — a read-only licence rejects writes here, and
      // a background that looks applied but isn't saved is worse than none.
      handleSettingChange(key, previous);
      throw error;
    }
  };

  // Backup settings take effect in the main-process scheduler, so persist
  // them immediately instead of waiting for the Save button.
  const persistBackupSetting = async (key, value) => {
    try {
      await persistSetting(key, value);
      await loadBackupInfo();
    } catch (error) {
      console.error('Error saving backup setting:', error);
    }
  };

  // Pedigree background. The image is compressed and downscaled here, then
  // stored immediately; the overlay strength follows the slider once the drag
  // ends (so a drag isn't one write per pixel).
  const handleBackgroundUpload = async (event) => {
    const file = event.target.files[0];
    event.target.value = ''; // let the same file be re-picked after a failure
    if (!file) return;

    setBackgroundBusy(true);
    setMessage({ type: '', text: '' });
    try {
      const dataUrl = await processBackgroundFile(file);
      await persistSetting(BACKGROUND_KEY, dataUrl);
      setMessage({ type: 'success', text: 'Background image saved — it will appear on every pedigree PDF.' });
    } catch (error) {
      console.error('Error saving pedigree background:', error);
      setMessage({ type: 'error', text: error.message || 'Could not use that image.' });
    } finally {
      setBackgroundBusy(false);
    }
  };

  const handleBackgroundRemove = async () => {
    setBackgroundBusy(true);
    setMessage({ type: '', text: '' });
    try {
      await persistSetting(BACKGROUND_KEY, '');
      setMessage({ type: 'success', text: 'Background image removed.' });
    } catch (error) {
      console.error('Error removing pedigree background:', error);
      setMessage({ type: 'error', text: error.message || 'Could not remove the background image.' });
    } finally {
      setBackgroundBusy(false);
    }
  };

  const handleFadeCommit = async (value) => {
    try {
      await persistSetting(BACKGROUND_FADE_KEY, String(clampFade(value)));
    } catch (error) {
      console.error('Error saving background overlay:', error);
    }
  };

  const handleBackupNow = async () => {
    setBackupBusy(true);
    try {
      const result = await window.electronAPI.backupNow();
      if (result && result.success) {
        setMessage({ type: 'success', text: 'Backup created.' });
      } else {
        setMessage({ type: 'error', text: (result && result.error) || 'Backup failed.' });
      }
      await loadBackupInfo();
    } finally {
      setBackupBusy(false);
    }
  };

  const handleChooseBackupFolder = async () => {
    const result = await window.electronAPI.showOpenDialog({
      title: 'Choose backup folder',
      properties: ['openDirectory', 'createDirectory']
    });
    if (!result.canceled && result.filePaths && result.filePaths[0]) {
      await persistBackupSetting('backupLocation', result.filePaths[0]);
    }
  };

  const handleOpenBackupsFolder = async () => {
    const result = await window.electronAPI.backupOpenFolder();
    if (result && result.success === false) {
      setMessage({ type: 'error', text: result.error || 'Could not open the backups folder.' });
    }
  };

  const handleOpenRestore = async () => {
    try {
      setBackupFiles(await window.electronAPI.backupList());
    } catch (error) {
      setBackupFiles([]);
    }
    setRestoreOpen(true);
  };

  const handleRestoreBackup = async (name) => {
    const confirmMsg =
      'Restore this backup? Your current data will be replaced with the selected snapshot. ' +
      'A safety copy of your current data is kept, and the app will restart.';
    if (!window.confirm(confirmMsg)) return;
    setRestoreBusy(true);
    const result = await window.electronAPI.backupRestore(name);
    if (result && result.success === false) {
      setRestoreBusy(false);
      setRestoreOpen(false);
      setMessage({ type: 'error', text: result.error || 'Restore failed. Your current data is unchanged.' });
    } else {
      setMessage({ type: 'info', text: 'Restoring — the app will restart…' });
    }
  };

  const handleCheckForUpdates = async () => {
    setUpdateBusy(true);
    setUpdateResult('');
    try {
      const result = await window.electronAPI.checkForUpdates();
      if (result.status === 'update-available') {
        setUpdateResult(`Version ${result.version} is available — downloading in the background. You'll be prompted to restart when it's ready.`);
      } else if (result.status === 'up-to-date') {
        setUpdateResult('You are on the latest version.');
      } else {
        setUpdateResult(result.message || 'Could not check for updates.');
      }
    } catch (error) {
      setUpdateResult('Could not check for updates.');
    } finally {
      setUpdateBusy(false);
    }
  };

  const handleRecheckLicense = async () => {
    setLicenseBusy(true);
    try {
      const result = await recheck();
      if (result && result.success === false) {
        setMessage({ type: 'error', text: result.error || (IS_WEB ? 'Could not check your plan.' : 'Could not reach the license server.') });
      } else {
        setMessage({ type: 'success', text: IS_WEB ? 'Plan checked.' : 'License refreshed.' });
      }
    } finally {
      setLicenseBusy(false);
    }
  };

  const handleOpenActivation = () => {
    setActivationKey('');
    setActivationFeedback(null);
    setActivationOpen(true);
  };

  const handleCloseActivation = () => {
    if (!activationBusy) setActivationOpen(false);
  };

  const handleActivateKey = async (event) => {
    event.preventDefault();
    const key = activationKey.trim();
    if (!key) {
      setActivationFeedback({
        severity: 'error',
        complete: false,
        message: 'Enter the activation key from your purchase email.'
      });
      return;
    }

    setActivationBusy(true);
    setActivationFeedback(null);
    try {
      const result = await activate(key);
      if (!result || result.success === false) {
        setActivationFeedback({
          severity: 'error',
          complete: false,
          message: (result && result.error) || 'That key could not be activated. Check the key and try again.'
        });
        return;
      }

      const feedback = getActivationFeedback(result.state);
      setActivationFeedback(feedback);
      if (feedback.complete) setActivationKey('');
    } catch (error) {
      setActivationFeedback({
        severity: 'error',
        complete: false,
        message: error.message || 'That key could not be activated. Check your connection and try again.'
      });
    } finally {
      setActivationBusy(false);
    }
  };

  const handleCancelSubscription = async () => {
    const confirmMsg =
      'Cancel your subscription? You keep full access until the end of the period you already paid for, after which the app becomes read-only.';
    if (!window.confirm(confirmMsg)) return;
    setLicenseBusy(true);
    try {
      const result = await cancelSubscription();
      if (result && result.success) {
        setMessage({ type: 'success', text: result.message || 'Subscription cancelled.' });
        // The web path already returns the refreshed state; only desktop
        // needs the explicit follow-up check.
        if (!result.state) await recheck();
      } else {
        setMessage({ type: 'error', text: (result && result.error) || 'Could not cancel the subscription.' });
      }
    } finally {
      setLicenseBusy(false);
    }
  };

  const handleDeactivate = async () => {
    const confirmMsg =
      'Deactivate this device? The app will return to the license screen, and the freed slot can be used on another computer.';
    if (!window.confirm(confirmMsg)) return;
    setLicenseBusy(true);
    try {
      const result = await deactivate();
      if (result && result.success === false) {
        setMessage({ type: 'error', text: result.error || 'Could not deactivate this device.' });
      }
    } finally {
      setLicenseBusy(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setMessage({ type: '', text: '' });
  };


  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Save all settings
      for (const [key, value] of Object.entries(settings)) {
        if (SAVE_ON_CHANGE_ONLY.has(key)) continue;
        await DatabaseService.setSetting(key, value);
      }

      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Error saving settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await DatabaseService.exportAllData();
      setMessage({ 
        type: 'success', 
        text: `Successfully exported ${result.count} birds!` 
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Error exporting data. Please try again.'
      });
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (licenseState && (licenseState.mode === 'readonly' || licenseState.mode === 'unlicensed')) {
      setMessage({ type: 'error', text: 'Importing needs an active license or trial — the app is currently read-only.' });
      event.target.value = '';
      return;
    }

    setImporting(true);
    setMessage({ type: '', text: '' });

    try {
      // Validate file first
      const validation = await DatabaseService.validateImportFile(file);
      
      if (!validation.valid) {
        setMessage({ 
          type: 'error', 
          text: `Invalid file: ${validation.error}` 
        });
        return;
      }

      // Confirm import
      const confirmMsg = `This will import ${validation.birdCount} birds. ` +
        `If your database is empty this restores the backup exactly; otherwise the birds are added alongside your existing records. Continue?`;
      if (!window.confirm(confirmMsg)) {
        setMessage({ type: 'info', text: 'Import cancelled.' });
        return;
      }

      // Read and import file
      const text = await file.text();
      const result = await DatabaseService.importData(text);

      setMessage({
        type: 'success',
        text: result.restored
          ? `Successfully restored ${result.imported} birds from backup!`
          : `Successfully imported ${result.imported} birds${result.skipped ? ` (${result.skipped} skipped)` : ''}!`
      });

      // Reload settings in case they were updated
      await loadSettings();
    } catch (error) {
      console.error('Error importing data:', error);
      // License blocks already carry a user-facing message; don't double-prefix.
      setMessage({
        type: 'error',
        text: error.isLicenseError ? error.message : `Error importing data: ${error.message}`
      });
    } finally {
      setImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight="bold" mb={4}>
        Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Loft Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Loft Information
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Loft Name"
                    value={settings.loftName}
                    onChange={(e) => handleSettingChange('loftName', e.target.value)}
                    placeholder="Your Loft Name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Loft Slogan"
                    value={settings.loftSlogan}
                    onChange={(e) => handleSettingChange('loftSlogan', e.target.value)}
                    placeholder="Excellence in Racing Pigeons"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Breeder Name"
                    value={settings.breederName}
                    onChange={(e) => handleSettingChange('breederName', e.target.value)}
                    placeholder="Your Full Name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={settings.loftPhone}
                    onChange={(e) => handleSettingChange('loftPhone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    rows={2}
                    value={settings.loftAddress}
                    onChange={(e) => handleSettingChange('loftAddress', e.target.value)}
                    placeholder="Street Address, City, State, ZIP"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={settings.loftEmail}
                    onChange={(e) => handleSettingChange('loftEmail', e.target.value)}
                    placeholder="contact@yourloft.com"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={settings.loftWebsite}
                    onChange={(e) => handleSettingChange('loftWebsite', e.target.value)}
                    placeholder="https://www.yourloft.com"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Pedigree Background */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <WallpaperIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Pedigree Background
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" mb={3}>
                Print every pedigree on a picture of your loft, your club crest or a paper
                texture. The image is always faded behind a white overlay so the pedigree
                details stay easy to read — use the slider to set how strong that overlay is.
              </Typography>

              <Grid container spacing={3}>
                {/* Live preview — same page shape and the same overlay maths
                    the exported PDF bakes in, so what is legible here is
                    legible on paper. */}
                <Grid item xs={12} sm={5}>
                  <Box
                    sx={{
                      aspectRatio: '210 / 297',
                      maxWidth: 220,
                      mx: 'auto',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'grey.400',
                      borderStyle: settings[BACKGROUND_KEY] ? 'solid' : 'dashed',
                      borderRadius: 1,
                      bgcolor: 'common.white',
                      color: 'common.black',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      ...backgroundStyles(settings[BACKGROUND_KEY], settings[BACKGROUND_FADE_KEY])
                    }}
                  >
                    {settings[BACKGROUND_KEY] ? (
                      <>
                        <Typography
                          sx={{ fontSize: '0.7rem', fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}
                        >
                          {(settings.loftName || 'YOUR LOFT NAME').toUpperCase()}
                        </Typography>
                        <Typography sx={{ fontSize: '0.55rem', textAlign: 'center', fontStyle: 'italic' }}>
                          {settings.loftSlogan || 'Preview of your pedigree page'}
                        </Typography>
                        {[0, 1, 2].map((row) => (
                          <Box
                            key={row}
                            sx={{ border: '1px solid', borderColor: 'common.black', px: 0.5, py: 0.25 }}
                          >
                            <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, lineHeight: 1.4 }}>
                              ZA 21 MOUN {250 + row}
                            </Typography>
                            <Typography sx={{ fontSize: '0.45rem', lineHeight: 1.4 }}>
                              Strain: Sample · Color: Blue Bar
                            </Typography>
                          </Box>
                        ))}
                      </>
                    ) : (
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          No background — pedigrees print on plain white.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={7}>
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      disabled={backgroundBusy}
                    >
                      {backgroundBusy
                        ? 'Working…'
                        : settings[BACKGROUND_KEY]
                        ? 'Change Image'
                        : 'Choose Image'}
                      <input type="file" accept="image/*" hidden onChange={handleBackgroundUpload} />
                    </Button>
                    {settings[BACKGROUND_KEY] ? (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleBackgroundRemove}
                        disabled={backgroundBusy}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </Box>

                  <Box mt={3}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Overlay strength: {clampFade(settings[BACKGROUND_FADE_KEY])}%
                    </Typography>
                    <Slider
                      value={clampFade(settings[BACKGROUND_FADE_KEY])}
                      min={MIN_FADE}
                      max={MAX_FADE}
                      step={5}
                      marks={[
                        { value: MIN_FADE, label: 'Bolder' },
                        { value: MAX_FADE, label: 'Fainter' }
                      ]}
                      disabled={!settings[BACKGROUND_KEY] || backgroundBusy}
                      onChange={(e, value) => handleSettingChange(BACKGROUND_FADE_KEY, String(value))}
                      onChangeCommitted={(e, value) => handleFadeCommit(value)}
                      valueLabelDisplay="auto"
                      sx={{ maxWidth: 320 }}
                    />
                  </Box>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      The overlay never drops below {MIN_FADE}%, so even a dark photo leaves the
                      ring numbers, names and notes readable in print. Landscape pictures are
                      cropped to the centre of the page.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Data Management */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                Data Management
              </Typography>
              
              <Typography variant="body2" color="text.secondary" mb={2}>
                Export your data as a backup or import data from a previous backup.
              </Typography>

              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  disabled={exporting}
                >
                  {exporting ? 'Exporting...' : 'Export All Data'}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={importing}
                >
                  {importing ? 'Importing...' : 'Import Data'}
                  <input
                    type="file"
                    accept=".json"
                    hidden
                    onChange={handleImport}
                  />
                </Button>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  {IS_WEB ? (
                    <>
                      <strong>Note:</strong> Your data is stored securely in the cloud on your
                      account. Export creates a backup file you can keep anywhere and import
                      again whenever you need it.
                    </>
                  ) : (
                    <>
                      <strong>Note:</strong> Your data is stored locally and remains safe.
                      Export creates a backup file you can save anywhere.
                    </>
                  )}
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* Automatic Backups */}
          {window.electronAPI && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <BackupIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Automatic Backups
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={String(settings.backupEnabled) !== 'false'}
                      onChange={(e) => persistBackupSetting('backupEnabled', e.target.checked)}
                    />
                  }
                  label="Back up my database automatically (daily)"
                />

                <Box display="flex" gap={1} alignItems="center" mt={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Backup folder"
                    value={settings.backupLocation || ''}
                    placeholder="Next to the database (default)"
                    InputProps={{ readOnly: true }}
                  />
                  <Button variant="outlined" onClick={handleChooseBackupFolder}>
                    Choose…
                  </Button>
                  {settings.backupLocation ? (
                    <Button onClick={() => persistBackupSetting('backupLocation', '')}>Reset</Button>
                  ) : null}
                </Box>

                <Box display="flex" gap={2} alignItems="center" mt={2}>
                  <TextField
                    size="small"
                    type="number"
                    label="Backups to keep"
                    value={settings.backupKeep || '10'}
                    onChange={(e) => handleSettingChange('backupKeep', e.target.value)}
                    onBlur={(e) => {
                      const n = Math.min(100, Math.max(1, parseInt(e.target.value, 10) || 10));
                      persistBackupSetting('backupKeep', String(n));
                    }}
                    sx={{ width: 150 }}
                    inputProps={{ min: 1, max: 100 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {backupInfo && backupInfo.lastBackupAt
                      ? `Last backup: ${new Date(backupInfo.lastBackupAt).toLocaleString()} · ${backupInfo.count} kept`
                      : 'No backups yet.'}
                  </Typography>
                </Box>

                {backupInfo && backupInfo.lastError ? (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      Last backup attempt failed: {backupInfo.lastError}
                    </Typography>
                  </Alert>
                ) : null}

                <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                  <Button
                    variant="outlined"
                    startIcon={<BackupIcon />}
                    onClick={handleBackupNow}
                    disabled={backupBusy}
                  >
                    {backupBusy ? 'Backing up…' : 'Back Up Now'}
                  </Button>
                  <Button variant="outlined" startIcon={<FolderOpenIcon />} onClick={handleOpenBackupsFolder}>
                    Open Backups Folder
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RestoreIcon />}
                    onClick={handleOpenRestore}
                  >
                    Restore from Backup…
                  </Button>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    Backups protect against mistakes and corruption on this computer. For protection
                    against a failing or lost computer, choose a folder inside OneDrive/Google Drive or
                    on an external drive.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          )}

        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Account (web app only) */}
          {IS_WEB && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={1}>
                  Account
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Signed in as:</strong> {accountEmail || '—'}
                </Typography>
                {licenseState?.account?.emailVerified ? (
                  <Chip label="Email verified" color="success" size="small" sx={{ mb: 1 }} />
                ) : (
                  <>
                    <Chip label="Email unverified" color="warning" size="small" />
                    <EmailVerificationPanel email={accountEmail} onVerified={reloadLicense} forTrial={false} />
                  </>
                )}
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Your birds and settings are stored on this account. Sign in from any
                  computer to reach them.
                </Typography>
                <Button size="small" variant="outlined" onClick={signOut}>
                  Sign out
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Plan and licence information */}
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  {IS_WEB ? 'Plan & Billing' : 'License Information'}
                </Typography>
              </Box>

              {licenseState ? (
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip
                      size="small"
                      label={
                        licenseState.devBypass ? 'Development' :
                        licenseState.mode === 'full' ? 'Active' :
                        licenseState.mode === 'trial' ? 'Free trial' :
                        licenseState.mode === 'past_due' ? 'Payment problem' :
                        licenseState.mode === 'readonly' ? 'Read-only' : 'Not activated'
                      }
                      color={
                        licenseState.mode === 'full' ? 'success' :
                        licenseState.mode === 'trial' ? 'info' :
                        licenseState.mode === 'past_due' ? 'warning' :
                        'default'
                      }
                    />
                    {licenseState.plan && licenseState.plan !== 'dev' && (
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {licenseState.plan} plan
                      </Typography>
                    )}
                  </Box>
                  {!IS_WEB && licenseState.licenseKey && (
                    <Typography variant="body2" gutterBottom>
                      <strong>License Key:</strong> {licenseState.licenseKey}
                    </Typography>
                  )}
                  {licenseState.email && (
                    <Typography variant="body2" gutterBottom>
                      <strong>{IS_WEB ? 'Billing email' : 'Email'}:</strong> {licenseState.email}
                    </Typography>
                  )}
                  {licenseState.expiresAt && licenseState.plan !== 'lifetime' && (
                    <Typography variant="body2" gutterBottom>
                      <strong>{licenseState.mode === 'trial' ? 'Trial ends' : 'Verified until'}:</strong>{' '}
                      {new Date(licenseState.expiresAt).toLocaleDateString()}
                    </Typography>
                  )}
                  {!licenseState.devBypass && (
                    <Box display="flex" flexDirection="column" gap={1} mt={2}>
                      {(licenseState.mode === 'trial' || licenseState.mode === 'readonly' || licenseState.mode === 'unlicensed') && (
                        <Button size="small" variant="contained" onClick={openPrompt}>
                          {licenseState.mode === 'trial'
                            ? 'Choose a paid plan'
                            : (IS_WEB ? 'Choose a plan' : 'Buy or activate license')}
                        </Button>
                      )}
                      {IS_WEB && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<KeyIcon />}
                          onClick={handleOpenActivation}
                          disabled={licenseBusy}
                          sx={{ minHeight: 44 }}
                        >
                          Activation key
                        </Button>
                      )}
                      <Button size="small" variant="outlined" onClick={handleRecheckLicense} disabled={licenseBusy}>
                        {IS_WEB ? 'Check plan now' : 'Re-check license now'}
                      </Button>
                      {(IS_WEB || licenseState.licenseKey) && ['monthly', 'annual'].includes(licenseState.plan) &&
                        ['full', 'past_due'].includes(licenseState.mode) && licenseState.status !== 'canceling' && (
                        <Button size="small" color="warning" onClick={handleCancelSubscription} disabled={licenseBusy}>
                          Cancel subscription
                        </Button>
                      )}
                      {licenseState.status === 'canceling' && (
                        <Typography variant="caption" color="text.secondary">
                          Subscription cancelled — full access continues until the end of the paid period.
                        </Typography>
                      )}
                      {!IS_WEB && licenseState.licenseKey && (
                        <Button size="small" color="error" onClick={handleDeactivate} disabled={licenseBusy}>
                          Deactivate this device
                        </Button>
                      )}
                      {!IS_WEB && (
                        <Typography variant="caption" color="text.secondary">
                          Licenses work on up to 2 of your own devices. Deactivate here to move to a new computer.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {IS_WEB ? 'Plan information not available' : 'License information not available'}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Application Info */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Application Information
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Version:</strong> {IS_WEB ? 'Web' : (appVersion || '—')}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Build:</strong> Production
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Database:</strong> {IS_WEB ? 'Cloud (your account)' : 'SQLite'}
              </Typography>
              {window.electronAPI && window.electronAPI.checkForUpdates && (
                <Box mt={1}>
                  <Button size="small" variant="outlined" onClick={handleCheckForUpdates} disabled={updateBusy}>
                    {updateBusy ? 'Checking…' : 'Check for Updates'}
                  </Button>
                  {updateResult && (
                    <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                      {updateResult}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>

              {message.text && (
                <Alert 
                  severity={message.type} 
                  sx={{ mt: 2 }}
                  onClose={() => setMessage({ type: '', text: '' })}
                >
                  {message.text}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Browser accounts can link a purchased key directly from Settings. */}
      {IS_WEB && (
        <Dialog
          open={activationOpen}
          onClose={handleCloseActivation}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Activate with a key</DialogTitle>
          <Box component="form" onSubmit={handleActivateKey}>
            <DialogContent dividers>
              {activationFeedback && (
                <Alert
                  severity={activationFeedback.severity}
                  sx={{ mb: 2 }}
                  onClose={activationFeedback.complete ? undefined : () => setActivationFeedback(null)}
                >
                  {activationFeedback.message}
                </Alert>
              )}
              {!activationFeedback?.complete && (
                <TextField
                  autoFocus
                  inputRef={activationInputRef}
                  fullWidth
                  required
                  label="Activation key"
                  placeholder="PPM-XXXX-XXXX-XXXX-XXXX"
                  value={activationKey}
                  onChange={(event) => {
                    setActivationKey(event.target.value.toUpperCase());
                    if (activationFeedback) setActivationFeedback(null);
                  }}
                  disabled={activationBusy}
                  helperText="Enter the key from your purchase email to link the plan to this account."
                  autoComplete="off"
                  inputProps={{ spellCheck: false, autoCapitalize: 'characters' }}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseActivation} disabled={activationBusy} sx={{ minHeight: 44 }}>
                {activationFeedback?.complete ? 'Done' : 'Cancel'}
              </Button>
              {!activationFeedback?.complete && (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={activationBusy || !activationKey.trim()}
                  startIcon={activationBusy ? <CircularProgress size={16} color="inherit" /> : <KeyIcon />}
                  sx={{ minHeight: 44 }}
                >
                  {activationBusy ? 'Activating...' : 'Activate key'}
                </Button>
              )}
            </DialogActions>
          </Box>
        </Dialog>
      )}

      {/* Restore-from-backup picker */}
      <Dialog
        open={restoreOpen}
        onClose={() => { if (!restoreBusy) setRestoreOpen(false); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Restore from backup</DialogTitle>
        <DialogContent dividers>
          {backupFiles.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No backups found yet. Use “Back Up Now” to create one.
            </Typography>
          ) : (
            <List dense>
              {backupFiles.map((file) => (
                <ListItemButton
                  key={file.name}
                  disabled={restoreBusy}
                  onClick={() => handleRestoreBackup(file.name)}
                >
                  <ListItemText
                    primary={new Date(file.mtimeMs).toLocaleString()}
                    secondary={`${file.name} · ${(file.size / 1048576).toFixed(1)} MB`}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="caption">
              Restoring replaces your current data with the selected snapshot. A safety copy of your
              current data is kept, and the app will restart.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreOpen(false)} disabled={restoreBusy}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
